import { CROP_MARK_LEN_PX, FOLD_LINE_EXTEND_PX, mmToPx } from './constants';
import type { CardSize, Drift, Orientation, PaperSize } from '../types';

export interface PanelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A folded-card spread laid flat on its (effective-orientation) paper.
 *  - firstPanel / secondPanel: canonical panel order. Portrait → left/right,
 *    landscape → top/bottom. The PDF/preview decides which slot image goes
 *    in each based on the SpreadFlip settings.
 *  - foldStart / foldEnd: the two endpoints of the fold line (vertical in
 *    portrait, horizontal in landscape). Already extended slightly past the
 *    spread for registration.
 *  - spread{X,Y,W,H}: the outer bounding rect (used by the card-border mark).
 *  - All coords are 300-DPI pixels with top-left origin; pdf-lib y-flipping
 *    is done at draw time.
 */
export interface SpreadLayout {
  firstPanel: PanelRect;
  secondPanel: PanelRect;
  foldStart: { x: number; y: number };
  foldEnd: { x: number; y: number };
  spreadX: number;
  spreadY: number;
  spreadW: number;
  spreadH: number;
  cropMarks: Array<{ x1: number; y1: number; x2: number; y2: number }>;
  orientation: Orientation;
}

/** Effective paper dims given the card's orientation. Portrait card lays on
 *  landscape paper (the CardSize default); landscape card lays on portrait
 *  paper (width/height swapped). */
export const effectivePaper = (paper: PaperSize, orientation: Orientation) =>
  orientation === 'landscape'
    ? {
        widthPx: paper.heightPx,
        heightPx: paper.widthPx,
        widthIn: paper.heightIn,
        heightIn: paper.widthIn,
      }
    : { widthPx: paper.widthPx, heightPx: paper.heightPx, widthIn: paper.widthIn, heightIn: paper.heightIn };

/** Effective card dims given orientation. CardSize is stored canonically with
 *  width = folded width (so 5×7 means a 5-wide, 7-tall folded portrait card).
 *  In landscape mode, the same card is treated as 7×5. */
export const effectiveCard = (card: CardSize, orientation: Orientation) =>
  orientation === 'landscape'
    ? {
        widthPx: card.heightPx,
        heightPx: card.widthPx,
        widthIn: card.heightIn,
        heightIn: card.widthIn,
      }
    : { widthPx: card.widthPx, heightPx: card.heightPx, widthIn: card.widthIn, heightIn: card.heightIn };

const buildCropMarks = (
  spreadX: number,
  spreadY: number,
  spreadW: number,
  spreadH: number,
  fold: { start: { x: number; y: number }; end: { x: number; y: number } },
  orientation: Orientation
) => {
  const len = CROP_MARK_LEN_PX;
  const right = spreadX + spreadW;
  const bottom = spreadY + spreadH;
  // 8 corner ticks (2 per corner: horizontal + vertical).
  const marks = [
    { x1: spreadX - len, y1: spreadY, x2: spreadX, y2: spreadY },
    { x1: spreadX, y1: spreadY - len, x2: spreadX, y2: spreadY },
    { x1: right, y1: spreadY, x2: right + len, y2: spreadY },
    { x1: right, y1: spreadY - len, x2: right, y2: spreadY },
    { x1: spreadX - len, y1: bottom, x2: spreadX, y2: bottom },
    { x1: spreadX, y1: bottom, x2: spreadX, y2: bottom + len },
    { x1: right, y1: bottom, x2: right + len, y2: bottom },
    { x1: right, y1: bottom, x2: right, y2: bottom + len },
  ];
  // Fold registration ticks: perpendicular to the fold, just outside the spread.
  if (orientation === 'portrait') {
    const fx = fold.start.x;
    marks.push(
      { x1: fx, y1: spreadY - len, x2: fx, y2: spreadY },
      { x1: fx, y1: bottom, x2: fx, y2: bottom + len }
    );
  } else {
    const fy = fold.start.y;
    marks.push(
      { x1: spreadX - len, y1: fy, x2: spreadX, y2: fy },
      { x1: right, y1: fy, x2: right + len, y2: fy }
    );
  }
  return marks;
};

const computeLayout = (
  paper: PaperSize,
  card: CardSize,
  orientation: Orientation,
  offsetPx: { dx: number; dy: number } = { dx: 0, dy: 0 }
): SpreadLayout => {
  const effP = effectivePaper(paper, orientation);
  const effC = effectiveCard(card, orientation);

  if (orientation === 'portrait') {
    // Panels side-by-side, vertical fold.
    const spreadW = effC.widthPx * 2;
    const spreadH = effC.heightPx;
    const spreadX = Math.round((effP.widthPx - spreadW) / 2) + offsetPx.dx;
    const spreadY = Math.round((effP.heightPx - spreadH) / 2) + offsetPx.dy;
    const foldX = spreadX + effC.widthPx;
    const fold = {
      start: { x: foldX, y: spreadY - FOLD_LINE_EXTEND_PX },
      end: { x: foldX, y: spreadY + spreadH + FOLD_LINE_EXTEND_PX },
    };
    return {
      firstPanel: { x: spreadX, y: spreadY, width: effC.widthPx, height: effC.heightPx },
      secondPanel: { x: foldX, y: spreadY, width: effC.widthPx, height: effC.heightPx },
      foldStart: fold.start,
      foldEnd: fold.end,
      spreadX,
      spreadY,
      spreadW,
      spreadH,
      cropMarks: buildCropMarks(spreadX, spreadY, spreadW, spreadH, fold, orientation),
      orientation,
    };
  }

  // Landscape: panels stacked top/bottom, horizontal fold.
  const spreadW = effC.widthPx;
  const spreadH = effC.heightPx * 2;
  const spreadX = Math.round((effP.widthPx - spreadW) / 2) + offsetPx.dx;
  const spreadY = Math.round((effP.heightPx - spreadH) / 2) + offsetPx.dy;
  const foldY = spreadY + effC.heightPx;
  const fold = {
    start: { x: spreadX - FOLD_LINE_EXTEND_PX, y: foldY },
    end: { x: spreadX + spreadW + FOLD_LINE_EXTEND_PX, y: foldY },
  };
  return {
    firstPanel: { x: spreadX, y: spreadY, width: effC.widthPx, height: effC.heightPx },
    secondPanel: { x: spreadX, y: foldY, width: effC.widthPx, height: effC.heightPx },
    foldStart: fold.start,
    foldEnd: fold.end,
    spreadX,
    spreadY,
    spreadW,
    spreadH,
    cropMarks: buildCropMarks(spreadX, spreadY, spreadW, spreadH, fold, orientation),
    orientation,
  };
};

export const computeOutsideLayout = (
  paper: PaperSize,
  card: CardSize,
  orientation: Orientation
): SpreadLayout => computeLayout(paper, card, orientation);

export const computeInsideLayout = (
  paper: PaperSize,
  card: CardSize,
  drift: Drift,
  orientation: Orientation
): SpreadLayout =>
  computeLayout(paper, card, orientation, {
    dx: mmToPx(drift.dxMm),
    dy: mmToPx(drift.dyMm),
  });

export interface FitRect {
  sx: number;
  sy: number;
  sWidth: number;
  sHeight: number;
}

// PIL ImageOps.fit equivalent: pick the largest panel-aspect rectangle that
// fits inside the source, positioned by (offsetX, offsetY) in 0..1 normalized
// coords. 0.5 / 0.5 is the classic center-crop default.
export const fitImageToPanel = (
  imgWidth: number,
  imgHeight: number,
  panelWidth: number,
  panelHeight: number,
  offsetX = 0.5,
  offsetY = 0.5
): FitRect => {
  const imgAspect = imgWidth / imgHeight;
  const panelAspect = panelWidth / panelHeight;
  let sWidth: number, sHeight: number;
  if (imgAspect > panelAspect) {
    sHeight = imgHeight;
    sWidth = Math.round(imgHeight * panelAspect);
  } else {
    sWidth = imgWidth;
    sHeight = Math.round(imgWidth / panelAspect);
  }
  const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
  const sx = Math.round((imgWidth - sWidth) * clamp01(offsetX));
  const sy = Math.round((imgHeight - sHeight) * clamp01(offsetY));
  return { sx, sy, sWidth, sHeight };
};

export const validateCardFitsPaper = (
  paper: PaperSize,
  card: CardSize,
  orientation: Orientation
): string | null => {
  const effP = effectivePaper(paper, orientation);
  const effC = effectiveCard(card, orientation);
  let spreadW: number;
  let spreadH: number;
  if (orientation === 'portrait') {
    spreadW = effC.widthPx * 2 + CROP_MARK_LEN_PX * 2;
    spreadH = effC.heightPx + CROP_MARK_LEN_PX * 2;
  } else {
    spreadW = effC.widthPx + CROP_MARK_LEN_PX * 2;
    spreadH = effC.heightPx * 2 + CROP_MARK_LEN_PX * 2;
  }
  if (spreadW > effP.widthPx || spreadH > effP.heightPx) {
    return `Your card is too big for ${paper.label} in ${orientation} orientation. Try a smaller card, a bigger paper, or flip orientation.`;
  }
  return null;
};
